import React from 'react'
import { scrollToRef } from './createOrderHelpers'
import { OrderItem } from '../../types/Order'
import { DeliveryProvider } from '../../types/DeliveryProviders'
import { getTomorrowStart } from '../../helpers/dateDefaults'
import { validateOrder } from '../../helpers/createOrderValidation'

interface UseUnsavedChangesParams {
  customerName: string
  customerPhone: string
  customerEmail: string
  productsToAdd: OrderItem[]
  storeComment: string
  customerComment: string
  deliveryTimeSlot: string | undefined
  selectedDeliveryMethod: DeliveryProvider | null
  deliveryAddress: string
  zipCode: string
  city: string
  showAddProduct: boolean
  updateProduct: boolean
  deliveryDate: Date
}

export function useUnsavedChanges(params: UseUnsavedChangesParams): boolean {
  const {
    customerName,
    customerPhone,
    customerEmail,
    productsToAdd,
    storeComment,
    customerComment,
    deliveryTimeSlot,
    selectedDeliveryMethod,
    deliveryAddress,
    zipCode,
    city,
    showAddProduct,
    updateProduct,
    deliveryDate,
  } = params

  return React.useMemo(() => {
    const defaultDelivery = getTomorrowStart()
    
    if (customerName || customerPhone || customerEmail) return true
    if (productsToAdd?.length > 0) return true
    if (storeComment || customerComment) return true
    if (deliveryTimeSlot) return true
    if (selectedDeliveryMethod) return true
    if (deliveryAddress || zipCode || city) return true
    if (showAddProduct || updateProduct) return true
    if (deliveryDate && deliveryDate.getTime() !== defaultDelivery.getTime()) return true
    
    return false
  }, [
    customerName,
    customerPhone,
    customerEmail,
    productsToAdd,
    storeComment,
    customerComment,
    deliveryTimeSlot,
    selectedDeliveryMethod,
    deliveryAddress,
    zipCode,
    city,
    showAddProduct,
    updateProduct,
    deliveryDate,
  ])
}

interface ValidationRefs {
  customerRef: React.RefObject<HTMLDivElement | null>
  productsRef: React.RefObject<HTMLDivElement | null>
  deliveryRef: React.RefObject<HTMLDivElement | null>
  deliveryMethodRef: React.RefObject<HTMLDivElement | null>
  homeDeliveryInfoRef: React.RefObject<HTMLDivElement | null>
}

interface ValidationCallbacks {
  setIsEmailValid: (valid: boolean) => void
  setIsPhoneValid: (valid: boolean) => void
  setDeliveryTimeSlotError: (error: boolean) => void
  setAddProductError: (error: boolean) => void
  setDeliveryInformationError: (error: boolean) => void
  setDeliveryMethodError: (error: boolean) => void
}

interface ValidateAndScrollParams {
  customerName: string
  customerEmail: string
  customerPhone: string
  deliveryTimeSlot: string | undefined
  productsToAdd: OrderItem[]
  selectedDeliveryMethod: DeliveryProvider | null
  deliveryAddress: string
  zipCode: string
  city: string
  refs: ValidationRefs
  callbacks: ValidationCallbacks
}

export function validateAndScrollToError(params: ValidateAndScrollParams): boolean {
  const { refs, callbacks } = params

  const { isValid, errors } = validateOrder({
    customerName: params.customerName,
    customerEmail: params.customerEmail,
    customerPhone: params.customerPhone,
    deliveryTimeSlot: params.deliveryTimeSlot,
    productsToAdd: params.productsToAdd,
    selectedDeliveryMethod: params.selectedDeliveryMethod,
    deliveryAddress: params.deliveryAddress,
    zipCode: params.zipCode,
    city: params.city,
  })

  // Set all error states
  callbacks.setIsEmailValid(errors.email)
  callbacks.setIsPhoneValid(errors.phone)
  callbacks.setDeliveryTimeSlotError(!errors.deliveryTime)
  callbacks.setAddProductError(!errors.products)
  callbacks.setDeliveryInformationError(!errors.homeDeliveryInfo)
  callbacks.setDeliveryMethodError(!errors.deliveryMethod)

  // Scroll to first error
  if (!isValid) {
    if (!errors.name || !errors.phone || !errors.email) {
      scrollToRef(refs.customerRef)
    } else if (!errors.products) {
      scrollToRef(refs.productsRef)
    } else if (!errors.deliveryTime) {
      scrollToRef(refs.deliveryRef, { behavior: 'smooth', block: 'start' })
    } else if (!errors.deliveryMethod) {
      scrollToRef(refs.deliveryMethodRef, { behavior: 'smooth', block: 'start' })
    } else if (!errors.homeDeliveryInfo) {
      scrollToRef(refs.homeDeliveryInfoRef, { behavior: 'smooth', block: 'start' })
    }
  }
  return isValid
}
